import { useState, useEffect, useRef } from 'react';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import axiosInstance from '../../api/axiosInstance';

// The combined Astern sales kit — company profile, installed projects, the Tier-1
// stack battlecards and third-party evidence in one A4 document. HTML comes from
// the [Authorize] endpoint (so it is never publicly reachable) and the media is
// resolved against this origin.
export default function SalesKit() {
    const [html, setHtml] = useState(null);
    const [error, setError] = useState('');
    const [fallback, setFallback] = useState(false);
    const blobUrlRef = useRef(null);
    const wroteRef = useRef(false);

    useEffect(() => {
        axiosInstance
            .get('/SalesKit/SalesKit', {
                responseType: 'text',
                params: { v: Date.now() },
                headers: { 'Cache-Control': 'no-cache' },
            })
            .then(res => {
                const raw = typeof res.data === 'string' ? res.data : '';
                setHtml(raw.replace(/__ASSETBASE__/g, window.location.origin));
            })
            .catch(() => setError('Unable to load the sales kit. Please make sure you are logged in.'));
    }, []);

    // The kit becomes the page itself (user 2026-09-13: "by default should [be]
    // printable version"). Being the TOP-LEVEL document is what makes Ctrl+P
    // paginate into A4 sheets — window.print() inside a same-origin iframe is
    // routed to the top-level document, so the app shell gets printed instead and
    // the kit lands on one page. Writing the document in place also avoids a
    // top-level navigation to a blob: URL, which browsers block. Leaving the page
    // (Back, or the address bar) reloads the app normally.
    useEffect(() => {
        if (!html || wroteRef.current) return;
        wroteRef.current = true;
        try {
            document.open();
            document.write(html);
            document.close();
        } catch {
            setFallback(true);   // keep the old in-app view rather than a blank screen
        }
    }, [html]);

    const openPrintable = () => {
        if (!html) return;
        if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = URL.createObjectURL(new Blob([html], { type: 'text/html;charset=utf-8' }));
        const w = window.open(blobUrlRef.current, '_blank', 'noopener');
        if (!w) alert('Your browser blocked the new tab. Allow pop-ups for this site, then try again.');
    };

    if (error) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', p: 3 }}>
                <Typography sx={{ fontFamily: "'Poppins', sans-serif", color: '#64748B' }}>{error}</Typography>
            </Box>
        );
    }

    // Fallback only: the in-app viewer, used if writing the document was refused.
    if (fallback && html) {
        return (
            <>
                <iframe
                    title="Astern Sales Kit"
                    srcDoc={html}
                    style={{ position: 'fixed', inset: 0, width: '100vw', height: '100vh', border: 'none' }}
                />
                <Button
                    onClick={openPrintable}
                    startIcon={<PrintIcon />}
                    variant="contained"
                    sx={{
                        position: 'fixed', top: 12, right: 16, zIndex: 2147483647,
                        fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '0.75rem',
                        textTransform: 'none', borderRadius: '8px',
                        backgroundColor: '#2BB6FF', color: '#04222E',
                        boxShadow: '0 2px 10px rgba(0,0,0,.28)',
                        px: { xs: 1.25, sm: 2 },
                        minWidth: { xs: 0, sm: 64 },
                        '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 }, ml: 0 },
                    }}
                >
                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                        Printable version
                    </Box>
                </Button>
            </>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, justifyContent: 'center', alignItems: 'center', height: '100vh', p: 3 }}>
            <CircularProgress />
            <Typography sx={{ fontFamily: "'Poppins', sans-serif", color: '#64748B', fontSize: '0.9rem' }}>
                Loading the sales kit…
            </Typography>
        </Box>
    );
}
